import React, { useState, useEffect } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";

import { Building, Trash2, Edit } from "lucide-react";

import { useFormik } from 'formik';
import * as Yup from 'yup';
import Lottie from 'lottie-react';
import { toast } from 'react-toastify';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';
import animPage from 'assets/anim/anim-pageload.json';

import ServiceMain from 'services/serviceMain';

export default function PaneRental(props) {

    const [rentals, setRentals] = useState([]);
    const [types, setTypes] = useState([]);
    const [filterType, setFilterType] = useState([]);

    const [activeType, setActiveType] = useState('');
    const [selectedRental, setSelectedRental] = useState(null);

    const [isOpenNewType, setIsOpenNewType] = useState(false);
    const [isOpenNewRental, setIsOpenNewRental] = useState(false);
    const [isOpenEditRental, setIsOpenEditRental] = useState(false);

    const [submit, setSubmit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selType, setSelType] = useState('all');

    const mainService = new ServiceMain();

    const initialValuesType = { title: '', type: '' };
    const initialValuesRental = { title: '' };
    const initialValuesEdit = { title: '', type: '', status: '' };

    const schemaNewType = Yup.object().shape({
        type: Yup.string().required('Type is required'),
        title: Yup.string().required('Title is required (for first rental)'),
    });

    const schemaNewRental = Yup.object().shape({
        title: Yup.string().required('Title is required'),
    });

    const schemaEditRental = Yup.object().shape({
        title: Yup.string().required('Title is required'),
        type: Yup.string().required('Type is required'),
    });

    const formikNewType = useFormik({
        initialValues: initialValuesType,
        validationSchema: schemaNewType,
        enableReinitialize: true,
        onSubmit: values => {
            handleAddRental(values.title, values.type);
        }
    });

    const formikNewRental = useFormik({
        initialValues: initialValuesRental,
        validationSchema: schemaNewRental,
        enableReinitialize: true,
        onSubmit: values => {
            handleAddRental(values.title, activeType);
        }
    });

    const formikEditRental = useFormik({
        initialValues: initialValuesEdit,
        validationSchema: schemaEditRental,
        enableReinitialize: true,
        onSubmit: values => {
            handleUpdateRental(values);
        }
    });

    const handleAddRental = (title, type) => {
        setSubmit(true);
        let body = {
            "path": "rental/addRental",
            "body": {
                "title": title,
                "type": type,
                "total": 0,
                "paid": 0,
                "pending": 0,
                "startTime": new Date(),
                "endTime": new Date(),
                "status": "none"
            }
        };

        mainService.getMain(body, props.outlet.orgid).then((res) => {
            setSubmit(false);
            if (res.status) {
                formikNewType.resetForm();
                formikNewRental.resetForm();
                setIsOpenNewType(false);
                setIsOpenNewRental(false);
                loadData();
                toast.success("Rental created successfully!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            } else {
                toast.error("Server error: " + (res.msg || "Unknown error"), { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        });
    }

    const handleUpdateRental = (values) => {
        setSubmit(true);
        let body = {
            "path": "rental/updateRental",
            "body": {
                "uid": selectedRental._id,
                "data": {
                    "title": values.title,
                    "type": values.type,
                }
            }
        };
        mainService.getMain(body, props.outlet.orgid).then((res) => {
            setSubmit(false);
            if (res.status) {
                formikEditRental.resetForm();
                setIsOpenEditRental(false);
                setSelectedRental(null);
                loadData();
                toast.success("Rental updated successfully!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            } else {
                toast.error("Server error: " + (res.msg || "Unknown error"), { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        });
    }

    const openAddRentalToType = (type) => {
        setActiveType(type);
        setIsOpenNewRental(true);
    }

    const openEditRental = (rental) => {
        if (rental.status !== 'none') {
            toast.error('Cannot edit active rental.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            return;
        }
        setSelectedRental(rental);
        formikEditRental.setValues({
            title: rental.title,
            type: rental.type,
            status: rental.status
        });
        setIsOpenEditRental(true);
    }

    const deleteTypeGroup = (type) => {
        const rentalsOfType = rentals.filter(r => r.type === type);
        let isDelete = true;

        rentalsOfType.forEach(unit => {
            if (unit.status !== 'none') {
                isDelete = false;
            }
        });

        if (isDelete) {
            if (window.confirm(`Are you sure you want to delete all rentals of type "${type}"?`)) {
                Promise.all(rentalsOfType.map(r => {
                    let body = {
                        "path": "rental/deleteRental",
                        "body": { "did": r._id }
                    };
                    return mainService.getMain(body, props.outlet.orgid);
                })).then(() => {
                    loadData();
                    toast.success('Group deleted successfully', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                }).catch(err => {
                    toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                });
            }
        } else {
            toast.error('Please clear all the bills for the type group.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
        }
    }

    const deleteRental = (rental) => {
        if (rental.status === 'none') {
            if (window.confirm(`Are you sure you want to delete "${rental.title}"?`)) {
                let body = {
                    "path": "rental/deleteRental",
                    "body": {
                        did: rental._id
                    }
                };
                mainService.getMain(body, props.outlet.orgid).then((res) => {
                    if (res.status) {
                        loadData();
                        toast.success("Rental deleted!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    } else {
                        toast.error("Server error, please try again!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    }
                });
            }
        } else {
            toast.error('Cannot delete active rental.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
        }
    }

    const getColor = (sts) => {
        switch (sts) {
            case 'active':
                return 'bg-green-200';
            case 'inprog':
                return 'bg-red-200';
            case 'none':
            default:
                return 'bg-white';
        }
    }

    const sortTable = (filter, currentRentals = rentals, currentTypes = types) => {
        setSelType(filter);
        if (filter === 'all') {
            setFilterType(currentTypes);
        } else {
            setFilterType([filter]);
        }
    }

    const loadData = () => {
        setLoading(true);
        (async function () {
            let body = {
                "path": "rental/getAllRental",
                "body": {
                    "filter": {},
                    "page": 1,
                    "size": 10000
                }
            };
            var res = await mainService.getMain(body, props.outlet.orgid);
            if (res.data) {
                const list = res.data.data || [];
                setRentals(list);

                const uniqueTypes = [...new Set(list.map(item => item.type))];
                setTypes(uniqueTypes);

                setFilterType(uniqueTypes);
                setSelType('all');
            }
            setLoading(false);
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    const getRentalsByType = (type) => {
        return rentals.filter(r => r.type === type);
    }

    return (
        <>
            {
                loading && <div className='flex items-center justify-center w-full h-full'>
                    <Lottie animationData={animPage} className="w-40 h-40" loop={true} />
                </div>
            }
            {
                isOpenNewType && <div className='overflow-y-auto overflow-x-hidden z-50 w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className="relative w-full max-w-2xl h-full md:h-auto mx-auto bg-white shadow-lg rounded-md mt-10 p-4">
                        <div className='flex items-center justify-between'>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Add Rental Type &nbsp;
                            </h3>
                            <span className='bg-gray-200 hover:bg-gray-100 w-8 h-8 rounded-full cursor-pointer flex items-center justify-center' onClick={() => {
                                setIsOpenNewType(false);
                                formikNewType.resetForm();
                            }}>
                                <i className="las la-times text-sm text-black"></i>
                            </span>
                        </div>
                        <hr className='mt-2' />
                        <form className='mt-4' onSubmit={formikNewType.handleSubmit} onReset={formikNewType.handleReset} noValidate="" autoComplete='off'>
                            <div className="mb-6">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Type Name<sup className="text-red-600">*</sup></label>
                                <input type="text" id="type" value={formikNewType.values.type} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={formikNewType.handleChange} placeholder="e.g. Locker, Swim Suit, etc." />
                                {(formikNewType.errors.type && formikNewType.touched.type) && <p className='text-xs text-red-400 mt-1'>{formikNewType.errors.type}</p>}
                            </div>
                            <div className="mb-6">
                                <label className="block mb-2 text-sm font-medium text-gray-600">First Rental Name<sup className="text-red-600">*</sup></label>
                                <input type="text" id="title" value={formikNewType.values.title} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={formikNewType.handleChange} placeholder="e.g. Locker 101" />
                                {(formikNewType.errors.title && formikNewType.touched.title) && <p className='text-xs text-red-400 mt-1'>{formikNewType.errors.title}</p>}
                            </div>
                            <div className='flex items-end justify-end'>
                                <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                    {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                    {!submit && "ADD"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            }
            {
                isOpenNewRental && <div className='overflow-y-auto overflow-x-hidden z-50 w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className="relative w-full max-w-2xl h-full md:h-auto mx-auto bg-white shadow-lg rounded-md mt-10 p-4">
                        <div className='flex items-center justify-between'>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Add Rental to {activeType} &nbsp;
                            </h3>
                            <span className='bg-gray-200 hover:bg-gray-100 w-8 h-8 rounded-full cursor-pointer flex items-center justify-center' onClick={() => {
                                setIsOpenNewRental(false);
                                formikNewRental.resetForm();
                            }}>
                                <i className="las la-times text-sm text-black"></i>
                            </span>
                        </div>
                        <hr className='mt-2' />
                        <form className='mt-4' onSubmit={formikNewRental.handleSubmit} onReset={formikNewRental.handleReset} noValidate="" autoComplete='off'>
                            <div className="mb-3">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Rental Name<sup className="text-red-600">*</sup></label>
                                <input type="text" id="title" value={formikNewRental.values.title} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={formikNewRental.handleChange} />
                                {(formikNewRental.errors.title && formikNewRental.touched.title) && <p className='text-xs text-red-400 mt-1'>{formikNewRental.errors.title}</p>}
                            </div>

                            <div className='flex items-end justify-end'>
                                <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                    {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                    {!submit && "ADD"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            }
            {
                isOpenEditRental && <div className='overflow-y-auto overflow-x-hidden z-50 w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className="relative w-full max-w-2xl h-full md:h-auto mx-auto bg-white shadow-lg rounded-md mt-10 p-4">
                        <div className='flex items-center justify-between'>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Edit Rental &nbsp;
                            </h3>
                            <span className='bg-gray-200 hover:bg-gray-100 w-8 h-8 rounded-full cursor-pointer flex items-center justify-center' onClick={() => {
                                setIsOpenEditRental(false);
                                formikEditRental.resetForm();
                            }}>
                                <i className="las la-times text-sm text-black"></i>
                            </span>
                        </div>
                        <hr className='mt-2' />
                        <form className='mt-4' onSubmit={formikEditRental.handleSubmit} onReset={formikEditRental.handleReset} noValidate="" autoComplete='off'>
                            <div className="mb-3">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Rental Name<sup className="text-red-600">*</sup></label>
                                <input type="text" id="title" value={formikEditRental.values.title} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={formikEditRental.handleChange} />
                                {(formikEditRental.errors.title && formikEditRental.touched.title) && <p className='text-xs text-red-400 mt-1'>{formikEditRental.errors.title}</p>}
                            </div>
                            <div className="mb-3">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Type<sup className="text-red-600">*</sup></label>
                                <input type="text" id="type" value={formikEditRental.values.type} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={formikEditRental.handleChange} />
                                {(formikEditRental.errors.type && formikEditRental.touched.type) && <p className='text-xs text-red-400 mt-1'>{formikEditRental.errors.type}</p>}
                            </div>

                            <div className='flex items-end justify-end'>
                                <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                    {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                    {!submit && "UPDATE"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            }
            <ScrollArea className="w-full h-full py-2 px-2">
                <div className="grid grid-cols-12 grid-gap-2">
                    <div className="col-span-12">
                        <div className='w-full min-h-screen bg-gray-100 rounded-md grid grid-cols-12 gap-2 p-2'>
                            <div className='col-span-2 h-full border-r p-2'>
                                <button className="w-full h-10 py-1 px-3 rounded-md bg-prime hover:bg-primeLight text-white text-sm" onClick={() => { setIsOpenNewType(true); }}><i class="las la-plus mr-1"></i> ADD TYPE</button>
                                <ul>
                                    <li className={`py-2 uppercase text-sm font-medium my-2 cursor-pointer rounded-md px-2 border border-dashed ${selType === 'all' ? 'text-prime border-prime bg-red-100' : 'hover:bg-gray-200'}`} onClick={() => {
                                        sortTable('all');
                                    }}>
                                        All Types
                                    </li>
                                    {
                                        types.length !== 0 && types.map((typeName, index) => {
                                            return <li className={`py-2 uppercase text-sm font-medium my-2 cursor-pointer rounded-md px-2 border border-dashed ${selType === typeName ? 'text-prime border-prime bg-red-100' : 'hover:bg-gray-200'}`} key={index}>
                                                <div className='flex items-center justify-between'>
                                                    <span className='flex-grow' onClick={() => { sortTable(typeName); }}>{typeName}</span>
                                                    <div className='cursor-pointer' onClick={() => { deleteTypeGroup(typeName); }}>
                                                        <Trash2 size={18} />
                                                    </div>
                                                </div>
                                            </li>
                                        })
                                    }
                                </ul>
                            </div>
                            <div className='col-span-10'>
                                {
                                    filterType.length !== 0 && filterType.map((typeName, index) => {
                                        const typeRentals = getRentalsByType(typeName);
                                        return <div className='w-full my-4 border border-gray-200 border-dashed p-1 rounded' key={index}>
                                            <div className='w-full'>
                                                <div className='flex items-center justify-between'>
                                                    <div className='flex items-center'>
                                                        <Building size={22} />
                                                        <h1 className='text-xl font-medium uppercase ml-1'>{typeName}</h1>
                                                    </div>
                                                    <div className='w-[28px] h-[28px] bg-prime flex items-center justify-center text-2xl font-medium rounded text-white cursor-pointer hover:bg-primeLight' onClick={() => { openAddRentalToType(typeName); }}>+</div>
                                                </div>
                                                <div className='my-2 h-[1px] bg-gray-300'></div>
                                            </div>
                                            <div className='w-full grid grid-cols-6 gap-2'>
                                                {
                                                    typeRentals.length === 0 && < div className='h-[114px] p-2 rounded shadow col-span-6 border border-dashed border-prime cursor-pointer hover:shadow-md flex items-center justify-center'>
                                                        <h1 className='text-sm font-medium text-prime'>No rentals found.</h1>
                                                    </div>
                                                }
                                                {
                                                    typeRentals.length !== 0 && typeRentals.map((rental, rIndex) => {
                                                        return < div className={`h-[130px] p-2 rounded shadow col-span-1 border border-dashed border-gray-400 cursor-pointer hover:shadow-md ${getColor(rental.status)}`} key={rIndex}>
                                                            <div className='flex justify-between items-center px-1'>
                                                                <div>
                                                                    <p className='text-xl font-medium font-serif'>{rental.title}</p>
                                                                    <p className='text-xs font-medium font-serif text-prime'>{rental.type}</p>
                                                                    <p className='text-base font-medium font-serif mt-1'>₹ {rental.pending ? rental.pending.toFixed(2) : '0.00'}</p>
                                                                </div>
                                                            </div>
                                                            <div className='bg-gray-200 h-[1px] my-2'></div>
                                                            <div className='flex items-center justify-between'>
                                                                <div className='text-gray-400 flex items-center'>
                                                                    <Edit size={18} className='cursor-pointer text-gray-500 hover:text-prime' onClick={() => { openEditRental(rental); }} />
                                                                </div>
                                                                <Trash2 size={18} className='cursor-pointer hover:text-red-500' onClick={() => {
                                                                    deleteRental(rental);
                                                                }} />
                                                            </div>
                                                        </div>
                                                    })
                                                }
                                            </div>
                                        </div>
                                    })
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollArea >
        </>
    )
}
